import {useCallback, useEffect, useMemo, useRef} from 'react'
import {basic_gtm_event} from '../../../custom-api/layers/default'

const REFINEMENT_DISALLOW_LIST = [
    'c_clEnabled',
    'c_enabledForCountry',
    'c_enabledForReg',
    'c_isPDPOnly',
    'c_isVisible',
    'c_skuEnabledForCountry',
    'c_skuIsPDPOnly',
    'c_visibleByAll',
    'c_visibleByCorporate',
    'c_visibleByEmployee',
]

const REFINEMENT_HIDE_FROM_BUTTON_COUNT_LIST = [
    'cgid',
    'c_skuIsPDPOnly',
    'price',
    'c_skuEnabledForCountry',
    'c_isPDPOnlyPurchasable',
    'c_visibleByAll',
]

//Add more in the regex in the future if needed
const escapeSpecialCharacters = (searchParams) => ({
    ...searchParams,
    refine: Object.fromEntries(
        Object.entries(searchParams.refine || {}).map(([attributeId, values]) => [
            attributeId,
            Array.isArray(values)
                ? values.map((value) =>
                      typeof value === 'string' ? value.replace(/&/g, '%26') : value,
                  )
                : typeof values === 'string'
                ? values.replace(/&/g, '%26')
                : values,
        ]),
    ),
})

const usePlpFilters = ({
    searchParams,
    stringifySearchParams,
    shopperContext,
    customer,
    navigate,
    pathnameNoLocale,
    isSearch,
    addLayer,
    productSearchResult,
    isBrandDelonghi,
    categoryData,
}) => {
    const activeFiltersCount = useMemo(() => {
        const selectedRefinements = productSearchResult?.selectedRefinements || {}
        let attIds = []
        for (let ref in selectedRefinements) {
            if (
                !attIds.includes(ref) &&
                !REFINEMENT_DISALLOW_LIST.includes(ref) &&
                !REFINEMENT_HIDE_FROM_BUTTON_COUNT_LIST.includes(ref)
            )
                attIds.push(ref)
        }
        return attIds.length
    }, [productSearchResult?.selectedRefinements])

    useEffect(() => {
        const isCorporate = shopperContext?.customQualifiers?.isCorporate === 'true'
        const isEmployee = customer?.c_isEmployee

        if (isCorporate || isEmployee) {
            const searchParamsCopy = {...searchParams}
            delete searchParamsCopy.offset
            delete searchParamsCopy.page
            delete searchParamsCopy.limit
            delete searchParamsCopy.sort

            let doRedirect = false

            if (isCorporate && searchParams?.refine?.corporate !== true) {
                doRedirect = true
                searchParamsCopy.refine['corporate'] = 'true'
            }
            if (isEmployee && searchParams?.refine?.employee !== true) {
                doRedirect = true
                searchParamsCopy.refine['employee'] = 'true'
            }

            if (doRedirect)
                navigate(
                    `${pathnameNoLocale}?${stringifySearchParams(searchParamsCopy)}`,
                    'replace',
                )
        }
    }, [shopperContext, customer])

    const categoryRefinements = useMemo(() => {
        return productSearchResult?.refinements?.filter(
            (ref) => ref.attributeId === 'c_productCategory',
        )
    }, [productSearchResult])

    const filterRefinements = (refinements) =>
        refinements.filter(({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)) || []

    const getInlineRefinements = (refinements) => {
        const refinementsList = filterRefinements(refinements)

        if (!isBrandDelonghi) return refinementsList

        return refinementsList.filter(({attributeId}) => attributeId !== 'c_productCategory')
    }

    const category = useRef()
    const filtersRef = useRef(getInlineRefinements(productSearchResult?.refinements || []))

    const setCurrentCategory = (currentCategory) => {
        category.current = currentCategory
    }

    useEffect(() => {
        if (productSearchResult?.refinements) {
            filtersRef.current = getInlineRefinements(productSearchResult.refinements)
        }
    }, [productSearchResult?.refinements])

    setCurrentCategory(categoryData)

    if (productSearchResult?.refinements && Array.isArray(productSearchResult.refinements)) {
        productSearchResult.refinements = filterRefinements(productSearchResult.refinements)
    }

    if (productSearchResult?.selectedRefinements) {
        productSearchResult.selectedRefinements = Object.fromEntries(
            Object.entries(productSearchResult.selectedRefinements).filter(
                ([key]) => !REFINEMENT_DISALLOW_LIST.includes(key),
            ),
        )
    }

    const toggleFilter = useCallback(
        async (
            value,
            attributeId,
            keyLabel,
            selected,
            allowMultiple = true,
            component = 'standard',
        ) => {
            const gtm_events = {
                sort: 'Sort filter',
                card: 'Card filter',
                quick: 'Quick filter',
                standard: 'Standard filter',
            }
            const filterEvent = gtm_events[component]
            // Deep-copy refine so we never mutate the live searchParams object
            const searchParamsCopy = {
                ...searchParams,
                refine: {...(searchParams.refine || {})},
            }

            delete searchParamsCopy.offset
            delete searchParamsCopy.page
            delete searchParamsCopy.limit

            const track = (eventAction, eventDetail) =>
                addLayer(
                    basic_gtm_event.bind(this, {
                        event: 'ga_event',
                        event_name: 'engagement',
                        eventCategory: filterEvent,
                        eventAction,
                        eventLabel: value?.value || keyLabel || '',
                        eventDetail: eventDetail || '',
                    }),
                )

            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                if (!selected && value.value !== previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                    track('filter_apply', value.label)
                }
            } else {
                let attributeValue = searchParamsCopy.refine[attributeId] || []

                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                } else {
                    attributeValue = [...attributeValue]
                }

                if (!selected) {
                    attributeValue.push(value.value)
                    track('filter_apply', value.value)
                } else {
                    attributeValue = attributeValue?.filter((v) => v !== value.value)
                }

                searchParamsCopy.refine[attributeId] = attributeValue

                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            if (selected) {
                track('filter_remove', value.value)
            }

            if (isSearch) {
                navigate(
                    `/search?${stringifySearchParams(escapeSpecialCharacters(searchParamsCopy))}`,
                )
            } else {
                navigate(
                    `${pathnameNoLocale}?${stringifySearchParams(
                        escapeSpecialCharacters(searchParamsCopy),
                    )}`,
                )
            }
        },
        [searchParams, isSearch, navigate, stringifySearchParams, pathnameNoLocale, addLayer],
    )

    const resetFilters = (onClose) => {
        const newSearchParams = {
            ...searchParams,
            refine: [],
        }
        delete newSearchParams.page
        const newPath = isSearch
            ? `/search?${stringifySearchParams(newSearchParams)}`
            : `${pathnameNoLocale}?${stringifySearchParams(newSearchParams)}`

        addLayer(
            basic_gtm_event.bind(this, {
                event: 'ga_event',
                event_name: 'engagement',
                eventCategory: 'engagement',
                eventAction: 'filter_remove',
                eventLabel: 'remove_all_filters',
            }),
        )

        navigate(newPath)
        onClose()
    }

    return {
        activeFiltersCount,
        categoryRefinements,
        category,
        toggleFilter,
        resetFilters,
    }
}

export default usePlpFilters
